import type { RendererBridge } from '@/modules/rendererBridge';
import type { RootState } from '@/store/rootState';
import type { AssetInstance } from '@/types/assetTypes';
import { selectKeyframesByAssetId } from '@/features/keyframes/keyframesSlice';
import { getFirstKeyframe } from '@/modules/keyframes/utils';

// A viewport deselect this soon after the editor selected a keyframe there is the renderer dropping its old selection
const OLD_SELECTION_DROPPED_MS = 500;

interface SelectionStore {
  getState(): RootState;
  subscribe(listener: () => void): () => void;
}

/** The keyframe the viewport holds selected, as far as the editor knows, with its asset. */
type Held = { keyframeId: string; assetId: string } | null;

/** What the editor has selected: a keyframe, clip, gate, reactive response or asset. */
export interface Selection {
  keyframeId: string | null;
  clipId: string | null;
  triggerId: string | null;
  reactiveAssetId: string | null;
  assetId: string | null;
}

export const selectionOf = (state: RootState): Selection => ({
  keyframeId: state.editorSelection.selectedKeyframeId,
  clipId: state.editorSelection.selectedClipId,
  triggerId: state.editorSelection.selectedTriggerId,
  reactiveAssetId: state.editorSelection.selectedReactiveAssetId,
  assetId: (state.sceneAssets.sceneAssetsArray as AssetInstance[]).find((asset) => asset.isSelected)?.instanceId ?? null,
});

export const sameSelection = (a: Selection, b: Selection) =>
  a.keyframeId === b.keyframeId && a.clipId === b.clipId && a.triggerId === b.triggerId
  && a.reactiveAssetId === b.reactiveAssetId && a.assetId === b.assetId;

/** Whether an asset's keyframes can be selected in the viewport: not while the asset or gate is locked or hidden. */
export function isSelectable(state: RootState, assetInstanceId: string): boolean {
  const asset = (state.sceneAssets.sceneAssetsArray as AssetInstance[]).find((a) => a.instanceId === assetInstanceId);
  if (asset && (asset.isLocked || asset.isVisible === false)) return false;
  return !state.triggers.entities[assetInstanceId]?.isLocked;
}

/** The selected gate whose keyframes the editor has yet to load, so which of them the viewport shows is not known yet. */
function loadingGate(state: RootState, selection: Selection): string | null {
  if (selection.clipId || selection.reactiveAssetId || !selection.triggerId) return null;
  const gate = state.triggers.entities[selection.triggerId];
  return gate && selectKeyframesByAssetId(state, gate.assetInstanceId).length === 0 ? gate.assetInstanceId : null;
}

/** The keyframe the viewport shows for the editor's selection: a keyframe of the selected asset, one just selected that it holds, a gate's first keyframe, or for an asset alone the one of its keyframes it holds; null for none. */
function shownKeyframe(state: RootState, selection: Selection, previous: Selection, held: Held): { keyframeId: string; assetId: string | null } | null {
  if (selection.reactiveAssetId) return null;
  if (selection.clipId) {
    return selection.assetId && held?.assetId === selection.assetId && isSelectable(state, held.assetId) ? held : null;
  }
  if (selection.triggerId) {
    const gate = state.triggers.entities[selection.triggerId];
    if (!gate) return null;
    if (held?.assetId === gate.assetInstanceId) return held;
    const first = getFirstKeyframe(selectKeyframesByAssetId(state, gate.assetInstanceId));
    return first ? { keyframeId: first.id, assetId: gate.assetInstanceId } : null;
  }
  if (!selection.keyframeId) {
    return selection.assetId && held?.assetId === selection.assetId && isSelectable(state, held.assetId) ? held : null;
  }
  if (held?.keyframeId === selection.keyframeId && previous.keyframeId !== selection.keyframeId) return held;
  const keyframe = state.keyframes.entities[selection.keyframeId];
  if (selection.assetId && keyframe && keyframe.assetInstanceId !== selection.assetId) return null;
  return { keyframeId: selection.keyframeId, assetId: keyframe?.assetInstanceId ?? selection.assetId };
}

/** Keeps the viewport on the editor's own selection: selectKeyframe for a keyframe it should show but doesn't hold, clearSelection (to a renderer that accepts it) for one it can't show. */
export function followSelection(store: SelectionStore, bridge: RendererBridge): () => void {
  let held: Held = null;
  let heldSince = 0;
  let rendererChose = false;
  let last = selectionOf(store.getState());
  let lastSelection = store.getState().editorSelection;
  let lastAssets = store.getState().sceneAssets.sceneAssetsArray;
  // The selected gate whose keyframes are loading, judged again once they arrive
  let loading: string | null = null;
  let checking = false;

  // Judged once every dispatch of one handler has landed, so a selection made in steps is taken whole
  const check = () => {
    checking = false;
    const theirs = rendererChose;
    rendererChose = false;
    const state = store.getState();
    const loaded = loading !== null && selectKeyframesByAssetId(state, loading).length > 0;
    if (!loaded && state.editorSelection === lastSelection && state.sceneAssets.sceneAssetsArray === lastAssets) return;
    lastSelection = state.editorSelection;
    lastAssets = state.sceneAssets.sceneAssetsArray;
    const selection = selectionOf(state);
    if (!loaded && sameSelection(selection, last)) return;
    const previous = last;
    last = selection;
    loading = theirs ? null : loadingGate(state, selection);
    if (theirs || loading) return;
    const shown = shownKeyframe(state, selection, previous, held);
    if (shown && held?.keyframeId === shown.keyframeId) return;
    if (shown && !shown.assetId) return;
    if (shown && isSelectable(state, shown.assetId)) {
      bridge.send({ type: 'selectKeyframe', keyframeUniqueId: shown.keyframeId, assetInstanceUniqueId: shown.assetId });
      return;
    }
    if (bridge.accepts('clearSelection')) bridge.send({ type: 'clearSelection' });
  };
  const checkSoon = () => {
    if (checking) return;
    checking = true;
    queueMicrotask(check);
  };

  const unsubscribe = store.subscribe(checkSoon);
  const sent = bridge.sent$.subscribe((message) => {
    if (message.type === 'selectKeyframe') {
      held = { keyframeId: message.keyframeUniqueId, assetId: message.assetInstanceUniqueId };
      heldSince = Date.now();
    } else if (message.type === 'clearSelection') held = null;
  });
  const selected = bridge.on('keyframeSelected').subscribe((message) => {
    held = { keyframeId: message.keyframeUniqueId, assetId: message.assetInstanceUniqueId };
    rendererChose = true;
    checkSoon();
  });
  const cleared = bridge.on('selectionCleared').subscribe(() => {
    if (Date.now() - heldSince >= OLD_SELECTION_DROPPED_MS) held = null;
    rendererChose = true;
    checkSoon();
  });

  return () => {
    unsubscribe();
    sent.unsubscribe();
    selected.unsubscribe();
    cleared.unsubscribe();
  };
}
